import clsx from 'clsx'
import { forwardRef, type ButtonHTMLAttributes, type ForwardedRef } from 'react'
import { Spinner } from './spinner'

type ButtonVariant = 'solid' | 'outline' | 'ghost' | 'soft' | 'white' | 'link'
type ButtonSize = 'small' | 'default' | 'big' | 'smallIcon' | 'icon' | 'bigIcon' | 'custom'
type ButtonColor = 'primary' | 'danger' | 'warning' | 'success'
type ButtonRoundedSide = 'none' | 'left' | 'right' | 'both'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: ButtonVariant
    size?: ButtonSize
    color?: ButtonColor
    roundedSide?: ButtonRoundedSide
    block?: boolean
    loading?: boolean
    ref?: ForwardedRef<HTMLButtonElement>
}

const buttonSize = {
    small: 'px-3 h-[2.375rem] w-fit',
    default: 'px-4 h-[2.875rem] w-fit',
    big: 'px-4 sm:px-5 h-[2.875rem] sm:h-[3.875rem] w-fit',
    smallIcon: 'h-[2.375rem] w-[2.375rem]',
    icon: 'h-[2.875rem] w-[2.875rem]',
    bigIcon: 'h-[2.875rem] w-[2.875rem] sm:h-[3.875rem] sm:w-[3.875rem]',
    custom: '',
}

const primary: Record<ButtonVariant, string> = {
    solid: 'border-transparent bg-primary-600 text-neutral-50 hover:bg-primary-700 ring-primary-400',
    outline:
        'border bg-transparent border-neutral-200 text-neutral-500 hover:border-primary-600 hover:text-primary-600 dark:border-neutral-700 dark:text-neutral-400 dark:hover:text-primary-500 dark:hover:border-primary-600 dark:focus:outline-none dark:focus:ring-primary-400 ring-primary-400',
    ghost: 'border-transparent text-primary-600 hover:bg-primary-100 hover:text-primary-800 disabled:opacity-50 disabled:pointer-events-none dark:text-primary-500 dark:hover:bg-primary-800/30 dark:hover:text-primary-400 ring-primary-400',
    soft: 'border-transparent bg-primary-100 text-primary-800 hover:bg-primary-200 ring-primary-400 dark:bg-primary-900/50 dark:text-primary-50 dark:hover:bg-primary-950 dark:ring-primary-400',
    white: 'border border-neutral-200 bg-white dark:bg-neutral-700 text-neutral-500 shadow-sm hover:bg-neutral-50 disabled:opacity-50 disabled:pointer-events-none dark:border-neutral-700 dark:text-white dark:hover:bg-neutral-700/50 hover:text-primary-600 dark:hover:text-primary-500 ring-primary-400',
    link: 'border-transparent text-primary-600 hover:text-primary-800 disabled:opacity-50 disabled:pointer-events-none dark:text-primary-500 dark:hover:text-primary-400 ring-primary-400',
}

const danger: Record<ButtonVariant, string> = {
    solid: 'border-transparent bg-danger-600 text-neutral-50 hover:bg-danger-700 ring-danger-400',
    outline:
        'border bg-transparent border-neutral-200 text-neutral-500 hover:border-danger-600 hover:text-danger-600 dark:border-neutral-700 dark:text-neutral-400 dark:hover:text-danger-500 dark:hover:border-danger-600 dark:focus:outline-none dark:focus:ring-danger-400 ring-danger-400',
    ghost: 'border-transparent text-danger-600 hover:bg-danger-100 hover:text-danger-800 disabled:opacity-50 disabled:pointer-events-none dark:text-danger-500 dark:hover:bg-danger-800/30 dark:hover:text-danger-400 ring-danger-400',
    soft: 'border-transparent bg-danger-100 text-danger-800 hover:bg-danger-200 ring-danger-400 dark:bg-danger-900/50 dark:text-danger-50 dark:hover:bg-danger-950 dark:ring-danger-400',
    white: 'border border-neutral-200 bg-white dark:bg-neutral-700 text-neutral-500 shadow-sm hover:bg-neutral-50 disabled:opacity-50 disabled:pointer-events-none dark:border-neutral-700 dark:text-white dark:hover:bg-neutral-700/50 hover:text-danger-600 dark:hover:text-danger-500 ring-primary-400',
    link: 'border-transparent text-danger-600 hover:text-danger-800 disabled:opacity-50 disabled:pointer-events-none dark:text-danger-500 dark:hover:text-danger-400 ring-danger-400',
}
const warning: Record<ButtonVariant, string> = {
    solid: 'border-transparent bg-warning-600 text-neutral-50 hover:bg-warning-700 ring-warning-400',
    outline:
        'border bg-transparent border-neutral-200 text-neutral-500 hover:border-warning-600 hover:text-warning-600 dark:border-neutral-700 dark:text-neutral-400 dark:hover:text-warning-500 dark:hover:border-warning-600 dark:focus:outline-none dark:focus:ring-warning-400 ring-warning-400',
    ghost: 'border-transparent text-warning-600 hover:bg-warning-100 hover:text-warning-800 disabled:opacity-50 disabled:pointer-events-none dark:text-warning-500 dark:hover:bg-warning-800/30 dark:hover:text-warning-400 ring-warning-400',
    soft: 'border-transparent bg-warning-100 text-warning-800 hover:bg-warning-200 ring-warning-400 dark:bg-warning-900/50 dark:text-warning-50 dark:hover:bg-warning-950 dark:ring-warning-400',
    white: 'border border-neutral-200 bg-white dark:bg-neutral-700 text-neutral-500 shadow-sm hover:bg-neutral-50 disabled:opacity-50 disabled:pointer-events-none dark:border-neutral-700 dark:text-white dark:hover:bg-neutral-700/50 hover:text-warning-600 dark:hover:text-warning-500 ring-primary-400',
    link: 'border-transparent text-warning-600 hover:text-warning-800 disabled:opacity-50 disabled:pointer-events-none dark:text-warning-500 dark:hover:text-warning-400 ring-warning-400',
}
const success: Record<ButtonVariant, string> = {
    solid: 'border-transparent bg-success-600 text-neutral-50 hover:bg-success-700 ring-success-400',
    outline:
        'border bg-transparent border-neutral-200 text-neutral-500 hover:border-success-600 hover:text-success-600 dark:border-neutral-700 dark:text-neutral-400 dark:hover:text-success-500 dark:hover:border-success-600 dark:focus:outline-none dark:focus:ring-success-400 ring-success-400',
    ghost: 'border-transparent text-success-600 hover:bg-success-100 hover:text-success-800 disabled:opacity-50 disabled:pointer-events-none dark:text-success-500 dark:hover:bg-success-800/30 dark:hover:text-success-400 ring-success-400',
    soft: 'border-transparent bg-success-100 text-success-800 hover:bg-success-200 ring-success-400 dark:bg-success-900/50 dark:text-success-50 dark:hover:bg-success-950 dark:ring-success-400',
    white: 'border border-neutral-200 bg-white dark:bg-neutral-700 text-neutral-500 shadow-sm hover:bg-neutral-50 disabled:opacity-50 disabled:pointer-events-none dark:border-neutral-700 dark:text-white dark:hover:bg-neutral-700/50 hover:text-success-500 dark:hover:text-success-400 ring-primary-400',
    link: 'border-transparent text-success-600 hover:text-success-800 disabled:opacity-50 disabled:pointer-events-none dark:text-success-600 dark:hover:text-success-500 ring-success-400',
}

const buttonStyle: Record<ButtonColor, Record<ButtonVariant, string>> = {
    primary,
    danger,
    warning,
    success,
}

const buttonRoundedSide = { none: 'rounded-none', left: 'rounded-l-lg', right: 'rounded-r-lg', both: 'rounded-lg' }

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref): JSX.Element => {
    const {
        children,
        variant = 'solid',
        size = 'default',
        color = 'primary',
        block = false,
        loading = false,
        disabled = false,
        roundedSide = 'both',
        className,
        ...otherProps
    } = props

    return (
        <button
            className={clsx(
                buttonSize[size],
                buttonStyle[color][variant],
                buttonRoundedSide[roundedSide],
                block ? 'flex w-full' : 'inline-flex',
                'border-1 items-center justify-center gap-x-2 text-sm font-semibold outline-none transition focus-visible:ring-4 disabled:pointer-events-none disabled:opacity-50',
                className,
            )}
            disabled={loading || disabled}
            ref={ref}
            {...otherProps}
        >
            {loading ? <Spinner size="sm" /> : null}
            {children}
        </button>
    )
})

Button.displayName = 'Button'

export { Button, buttonSize, buttonStyle, type ButtonColor, type ButtonProps, type ButtonSize, type ButtonVariant }
